import { useState } from 'react';
import { Modal } from 'react-bootstrap';
import isEmail from 'validator/es/lib/isEmail';
import type { VoidFunctionComponent } from 'react';

import { useCreateUserInvitations } from '../../hooks/api/userInvitations/useCreateUserInvitations';
import { getErrorMessage } from '../../ReactQuery';
import { LabeledMultiTextInput, LoaderButton } from '../generic';
import Button, { ButtonGroup } from '../generic/Button';
import Text from '../generic/Text';

import styles from './ModalInviteUsers.module.less';

type Props = {
  organizationId: number;
  show: boolean;
  onHide: () => void;
};

/**
 * Whether the entered emails are valid to send: non-empty and every entry is a
 * valid email address.
 */
const areInviteEmailsValid = (emails: string[]): boolean =>
  emails.length > 0 && emails.every((email) => isEmail(email.trim()));

/**
 * Modal for inviting users to an organization by entering one or more email
 * addresses.
 */
export const ModalInviteUsers: VoidFunctionComponent<Props> = ({ organizationId, show, onHide }) => {
  const [emails, setEmails] = useState<string[]>([]);

  const { mutate, isLoading, isError, error, reset } = useCreateUserInvitations(organizationId);

  const canSend = areInviteEmailsValid(emails);

  const handleHide = () => {
    setEmails([]);
    reset();
    onHide();
  };

  const handleEmailsChange = (newEmails: string[]) => {
    setEmails(newEmails);
    //  Clear any stale inline error from a previous failed send as soon as the
    //  user edits the list, so it does not linger while they fix the input.
    if (isError) {
      reset();
    }
  };

  const handleSend = () => {
    const uniqueEmails = new Set(emails.map((email) => email.trim()));
    mutate(uniqueEmails, { onSuccess: handleHide });
  };

  return (
    <Modal show={show} onHide={handleHide}>
      <Modal.Header>
        <Text.H1>Invite Users</Text.H1>
      </Modal.Header>
      <Modal.Body>
        <LabeledMultiTextInput
          id="invite-users-emails"
          size="large"
          label="Email Address(es)"
          placeholder="Enter an email address and press enter"
          values={emails}
          onChange={handleEmailsChange}
          isCaseInsensitive
        />
        {emails.length > 0 && !canSend && (
          <div className={styles.error}>
            Please enter valid email addresses.
          </div>
        )}
        {isError && (
          <div className={styles.error}>
            {getErrorMessage(error, 'Could not send invitations.')}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <ButtonGroup>
          <Button type="secondary" size="medium" onClick={handleHide}>
            Close
          </Button>
          <LoaderButton
            type="primary"
            size="medium"
            testId="invite-users-send-button"
            isLoading={isLoading}
            isDisabled={!canSend || isLoading}
            onClick={handleSend}
          >
            Send Invite
          </LoaderButton>
        </ButtonGroup>
      </Modal.Footer>
    </Modal>
  );
};
